import React, { useRef } from "react";
import { Control, Controller } from "react-hook-form";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import { FormInput } from "@/components/bnp-ui";

const libraries: "places"[] = ["places"];

interface PlacesAutocompleteProps {
  control: Control<any>;
  errors: any;
  country?: string;
  defaultValue?: string;
  setLatLng?: (lat: number, lng: number) => void;
}

const PlacesAutocomplete: React.FC<PlacesAutocompleteProps> = ({
  control,
  errors,
  country = "NG",
  defaultValue = "Lagos",
  setLatLng,
}) => {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY as string,
    libraries,
  });

  const autocompleteOptions = {
    componentRestrictions: { country: country },
    fields: ["address_components", "formatted_address"],
    types: ["address"],
  };

  return (
    <Controller
      name="address"
      control={control}
      defaultValue={defaultValue}
      render={({ field: { onChange, onBlur, value } }) => (
        <div className="relative">
          {isLoaded ? (
            <Autocomplete
              options={autocompleteOptions}
              onLoad={(autocomplete) => {
                autocompleteRef.current = autocomplete;
              }}
              onPlaceChanged={() => {
                const place = autocompleteRef.current?.getPlace();
                if (place?.formatted_address) {
                  onChange(place.formatted_address);
                }
                if (place?.geometry?.location && setLatLng) {
                  setLatLng(
                    place.geometry.location.lat(),
                    place.geometry.location.lng(),
                  );
                }
              }}
            >
              <div>
                <FormInput
                  type="text"
                  required
                  name="address"
                  value={value}
                  label="Location"
                  placeholder="e.g. Orchid road, Lagos, Nigeria"
                  className="min-w-[140px] max-w-[140px] lg:w-[398px] lg:max-w-[398px]"
                  onChange={(e) => onChange(e.target.value)}
                />
                {errors?.address && (
                  <p className="text-red-500 text-sm">
                    {errors?.address?.message as string}
                  </p>
                )}
              </div>
            </Autocomplete>
          ) : (
            <div>
              <FormInput
                hero
                type="text"
                name="address"
                value={value}
                label="Location"
                placeholder="Enter location"
                className="min-w-[140px] max-w-[140px] lg:min-w-[200px] lg:max-w-[200px]"
                onChange={(e) => onChange(e.target.value)}
                disabled={!isLoaded}
              />
              {!isLoaded && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                </div>
              )}
            </div>
          )}
          {loadError && (
            <p className="text-red-500 text-sm">
              Error loading Google Maps API
            </p>
          )}
          {errors?.location && (
            <p className="text-red-500 text-sm">
              {errors?.location?.message as string}
            </p>
          )}
        </div>
      )}
    />
  );
};

export default PlacesAutocomplete;
